/**
 * Cart Drawer Upsell Component
 *
 * Handles add-to-cart for upsell products inside the cart drawer.
 * Dispatches a CartAddEvent-compatible CustomEvent so cart-items-component
 * picks up the change and re-renders the drawer via the section rendering API.
 */


import { announceCartLinesUpdate } from '@theme/cart-events';
import { addBundleToCart } from '@theme/bundle-add-to-cart';
class CartDrawerUpsell extends HTMLElement {
  #resizeObserver = new ResizeObserver(() => this.#updateArrows());

  // The drawer morph patches this element in place (no reconnect) and resets the arrows to their
  // server-rendered `disabled`, so recompute whenever its content or those attributes change.
  #mutationObserver = new MutationObserver(() => this.#updateArrows());

  connectedCallback() {
    this.addEventListener('click', this.#handleClick.bind(this));
    this.addEventListener('scroll', () => this.#updateArrows(), { capture: true, passive: true });
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
    this.#updateArrows();
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  #updateArrows() {
    const track = this.querySelector('.cart-drawer-upsell__track');

    if (!track) {
      return;
    }

    const maxScroll = track.scrollWidth - track.clientWidth;

    for (const arrow of this.querySelectorAll('[data-upsell-arrow]')) {
      const atEdge = arrow.dataset.upsellArrow === '-1' ? track.scrollLeft <= 1 : track.scrollLeft >= maxScroll - 1;
      const disabled = maxScroll <= 1 || atEdge;

      if (arrow.disabled !== disabled) {
        arrow.disabled = disabled;
      }
    }
  }

  /**
   * Scroll the track by one card in the arrow's direction.
   * @param {HTMLElement} arrow
   */
  #scrollByCard(arrow) {
    const track = this.querySelector('.cart-drawer-upsell__track');
    const card = track?.querySelector('.cart-drawer-upsell__card');

    if (!track || !card) {
      return;
    }

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollBy({
      left: Number(arrow.dataset.upsellArrow) * (card.offsetWidth + gap),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  /**
   * @param {MouseEvent} event
   */
  async #handleClick(event) {
    const arrow = event.target.closest('[data-upsell-arrow]');

    if (arrow) {
      this.#scrollByCard(arrow);
      return;
    }

    // Handle Quick Add button → open overlay
    const quickAddBtn = event.target.closest('[data-open-quick-add]');

    if (quickAddBtn && !quickAddBtn.disabled) {
      this.#openQuickAdd(quickAddBtn);
      return;
    }

    const button = event.target.closest('[data-variant-id], [data-bundle-add]');

    if (!button || button.disabled) {
      return;
    }

    const variantId = Number(button.dataset.variantId);
    const bundle = this.#getBundle(button);

    if (!variantId && !bundle) {
      return;
    }

    button.classList.add('is-loading');
    const originalText = button.textContent;
    button.textContent = '...';
    button.disabled = true;

    try {
      if (bundle) {
        await addBundleToCart(bundle, { sourceId: 'cart-drawer-upsell' });
      } else {
        await this.#addVariant(variantId, Number(button.dataset.sellingPlan) || null);
      }

      button.textContent = 'Added!';
      setTimeout(() => {
        button.textContent = originalText;
      }, 1200);
    } catch (error) {
      console.error('[cart-drawer-upsell]', error.message);
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1500);
    } finally {
      button.classList.remove('is-loading');
    }
  }

  /**
   * @param {number} variantId
   * @param {number | null} sellingPlanId
   */
  async #addVariant(variantId, sellingPlanId) {
    /** @type {{id: number, quantity: number, selling_plan?: number}} */
    const item = { id: variantId, quantity: 1 };

    if (sellingPlanId) {
      item.selling_plan = sellingPlanId;
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [item],
        sections: this.#getSectionIds(),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || 'Failed to add item');
    }

    const data = await response.json();

    await announceCartLinesUpdate({
      action: 'add',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      source: 'cart-drawer-upsell',
      sourceId: 'cart-drawer-upsell',
      sections: data.sections,
      // `/cart/add.js` returns the added line items with their merged quantity — enough for
      // `detail.items`, so the announcement can resolve without a second round trip.
      items: data.items,
      variantId: String(variantId),
    });
  }

  /**
   * The pair a bundle card adds in place of its own variant, as on the product page.
   * @param {HTMLElement} button
   * @returns {import('./bundle-add-to-cart.js').BundleCart | null}
   */
  #getBundle(button) {
    const productId = button.dataset.bundleAdd;
    const jsonScript = productId && this.querySelector(`script[data-bundle-json="${productId}"]`);

    if (!jsonScript) {
      return null;
    }

    try {
      return JSON.parse(jsonScript.textContent);
    } catch (error) {
      console.error('[cart-drawer-upsell] Failed to parse bundle JSON', error);
      return null;
    }
  }

  /**
   * Open the Quick Add overlay for a multi-variant product.
   * Reads product JSON from the sibling <script> tag.
   * @param {HTMLButtonElement} button
   */
  #openQuickAdd(button) {
    const productId = button.dataset.openQuickAdd;
    const jsonScript = this.querySelector(`script[data-product-json="${productId}"]`);

    if (!jsonScript) {
      return;
    }

    try {
      const productData = JSON.parse(jsonScript.textContent);
      const quickAddEl = document.querySelector('cart-drawer-quick-add');

      if (quickAddEl && typeof quickAddEl.open === 'function') {
        quickAddEl.open(productData);
      }
    } catch (error) {
      console.error('[cart-drawer-upsell] Failed to parse product JSON', error);
    }
  }

  /**
   * Get section IDs for section rendering API.
   * Cart items component listens for sections to update the drawer.
   * @returns {string}
   */
  #getSectionIds() {
    const cartItemsComponent = this.closest('cart-items-component');

    if (cartItemsComponent) {
      const sectionId = cartItemsComponent.dataset.sectionId;

      if (sectionId) {
        return sectionId;
      }
    }

    return '';
  }
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
